import { api } from '../lib/api'
import { AgendaEvent, Schedule } from '../lib/agenda'

export type CalendarEvent = {
  url_clinical_record: string
  class: string
  patient_id: number
  professional_id: number
  box_calendar_id: number
  patient_rut: string
  patient_name: string
  patient_phone: string
  patient_email: string
  title: string
  description: string
  start: string
  time: string
  professional: string
  box: string
  end: string
  color: string
  id: string
  event_calendar_id: number
  editable: boolean
}

export type CalendarData = {
  events: CalendarEvent[]
  boxes: { id: number; name: string }[]
  professionals: { id: number; name: string }[]
  patients: { id: number; name: string; rut: string | null }[]
  selected_box_id: number | null
  session_duration: number
  session_max_blocks: number
  session_durations: number[]
  trial_features_enabled: boolean
  has_professional_without_schedule: boolean
}

export type ProfessionalScheduleEntry = {
  schedule: Schedule | null
  professional: { id: number; name: string; event_calendars: AgendaEvent[] }
}

export type CalendarFilters = {
  readonly box: string
  readonly professional: string
  readonly patient: string
  readonly start?: string
  readonly end?: string
}

export function fetchCalendarData(healthCenterId: number, filters: CalendarFilters): Promise<CalendarData> {
  const params = new URLSearchParams()
  if (filters.box) {
    params.set('box', filters.box)
  }
  if (filters.professional) {
    params.set('professional', filters.professional)
  }
  if (filters.patient) {
    params.set('patient', filters.patient)
  }
  if (filters.start && filters.end) {
    params.set('start', filters.start)
    params.set('end', filters.end)
  }
  return api.get<CalendarData>(`/api/health_centers/${healthCenterId}/calendar_events?${params.toString()}`)
}

export function fetchProfessionalSchedules(healthCenterId: number): Promise<ProfessionalScheduleEntry[]> {
  return api.get<ProfessionalScheduleEntry[]>(`/health_centers/${healthCenterId}/professionals/schedules`)
}

export type EventCalendarPayload = {
  box_calendar_id: number
  patient_id: number | string
  professional_id: number | string
  start_at_date: string
  start_at_time: string
  duration: number | string
  description: string
  patient_info?: { name: string; rut: string; email: string; phone_number: string }
}

export type EventCalendarResult = { errors: string | null; event_calendar: CalendarEvent | null; message?: string }

export function createEventCalendar(healthCenterId: number, payload: EventCalendarPayload): Promise<EventCalendarResult> {
  return api.post<EventCalendarResult>(`/health_centers/${healthCenterId}/event_calendars.json`, { event_calendar: payload })
}

export function updateEventCalendar(healthCenterId: number, id: number, payload: EventCalendarPayload): Promise<EventCalendarResult> {
  return api.patch<EventCalendarResult>(`/health_centers/${healthCenterId}/event_calendars/${id}.json`, { event_calendar: payload })
}

export function deleteEventCalendar(healthCenterId: number, id: number): Promise<{ errors: string | null; message: string; id?: string }> {
  return api.delete(`/health_centers/${healthCenterId}/event_calendars/${id}.json`)
}

export function fetchPatientContact(
  healthCenterId: number,
  patientId: number | string,
): Promise<{ id?: number; rut?: string; name?: string; email?: string; phone_number?: string }> {
  return api.get(`/health_centers/${healthCenterId}/fetch_patient/${patientId}.json`)
}

export type ProfessionalCalendar = { schedule: Schedule; professional: { id: number; name: string; rut: string } }

export function fetchProfessionalCalendar(healthCenterId: number, professionalId: string): Promise<ProfessionalCalendar> {
  return api.get<ProfessionalCalendar>(`/health_centers/${healthCenterId}/professionals/${professionalId}/calendar.json`)
}

export function createScheduleInterval(
  healthCenterId: number,
  professionalId: number,
  day: string,
  startAt: string,
  endAt: string,
): Promise<{ ok?: boolean; errors?: string }> {
  return api.post(`/health_centers/${healthCenterId}/professionals/${professionalId}/schedules.json`, {
    schedule: { day, start_at: startAt, end_at: endAt },
  })
}

export function updateScheduleInterval(
  healthCenterId: number,
  professionalId: number,
  day: string,
  id: number,
  startAt: string,
  endAt: string,
): Promise<{ ok?: boolean; errors?: string }> {
  return api.patch(`/health_centers/${healthCenterId}/professionals/${professionalId}/schedules/${id}.json?day=${day}`, {
    schedule: { day, id, start_at: startAt, end_at: endAt },
  })
}

export function deleteScheduleInterval(
  healthCenterId: number,
  professionalId: number,
  day: string,
  id: number,
): Promise<{ ok?: boolean; errors?: string }> {
  return api.delete(`/health_centers/${healthCenterId}/professionals/${professionalId}/schedules/${id}.json?day=${day}`)
}
